import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  HdButton,
  HdIcon,
  HdIconButton,
  HdRbacButton,
  HdTooltip
} from '../../../components';
import { numFormatter } from '../../../core/utils/number';
import {
  TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORD_SHOW_SAMPLE_CLICK,
  TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_DOWNLOAD_CLICK
} from '../tracking';
import { SyncRecordFailure } from '../ActivationSyncHistory/models/sync-failure';
import { capitalise } from '../../../core/utils/string';
import { HdDeterminateProgressBar } from '../../../progress-indicators/components/determinate-progress-bar/HdDeterminateProgressBar';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import styles from './styles.module.scss';
import { SyncRecordFailuresFactory } from '../ActivationSyncHistory/factory';
import { RecordsFailureContext } from './recordsFailureContext';
import { PayloadViewerDialog } from '../../../modal/components/payload-viewer-dialog/PayloadViewerDialog';
import HdModal from '../../../modal/components/modal/HdModal';
import { ConfirmDialogV1 } from '../../../modal/components/confirm-dialog-v1/ConfirmDialogV1';
import { RbacPermissions } from '../../../core/interfaces/user';
import ScrollToTopButton from '../../../a11y/scroll/components/scroll-to-top-button/ScrollToTopButton';
import useDownloader from './useDownloader';

interface RecordsFailureProps {
  activationRunId?: number;
  getRecordFailures?: Function;
  downloadFailureDetails?: any;
  showDownloadOption?: boolean;
  failureScope?: string;
  queryRecordsClick?: Function;
}

const dataIdGenerator = getDataIdGenerator('recordFailure');

function RecordsFailure({
  activationRunId,
  getRecordFailures,
  showDownloadOption = true,
  downloadFailureDetails,
  failureScope = 'Failed',
  queryRecordsClick
}: RecordsFailureProps) {
  const [totalFailuresCount, setTotalFailuresCount] = useState<number>();
  const [syncRecordFailures, setSyncRecordFailures] = useState<any[]>([]);
  const [syncRecordFailureSampleData, setSyncRecordFailureSampleData] = useState<any>(null);
  const [isCancelDownloadDialogOpen, setIsCancelDownloadDialogOpen] = useState<boolean>(false);

  const editorRef = useRef(null);

  const {
    downloadPercentage,
    downloading,
    start,
    cancel: cancelDownload
  } = useDownloader(downloadFailureDetails, {
    type: 'text/csv;charset=utf-8;',
    filename: 'error_report.csv'
  });

  const analyticsTracker = useAnalyticsTracker();
  const { loading, failureDetails, error } = useContext(RecordsFailureContext);

  useEffect(() => {
    getRecordFailures();
  }, []);

  useEffect(() => {
    if (failureDetails) {
      setSyncRecordFailures(SyncRecordFailuresFactory(failureDetails));
      setTotalFailuresCount(failureDetails.failureCount);
    }
  }, [failureDetails]);

  const downloadErrorReport = () => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_SYNC_FAILURES_DOWNLOAD_CLICK,
      properties: {
        activationRunId
      }
    });
    start();
  };

  const cancelDownloadReport = () => {
    setIsCancelDownloadDialogOpen(true);
  };

  const getRunFailureStage = (syncRecordFailure: SyncRecordFailure) =>
    capitalise(syncRecordFailure.phase);

  const showSample = (sampleData: any) => {
    analyticsTracker.eventTrack({
      action: TRACKER_ACTIVATION_SYNC_HISTORY_FAILED_RECORD_SHOW_SAMPLE_CLICK,
      properties: {
        activationRunId
      }
    });

    setSyncRecordFailureSampleData(sampleData);
  };

  const isTotalFailuresCountMoreThan100 = useMemo(
    () => totalFailuresCount > 100,
    [totalFailuresCount]
  );

  return (
    <div className='card-table' data-id={dataIdGenerator('')}>
      <div className={`card-table-header px-4 ${styles.cardTableHeader}`}>
        <div
          className={`card-table-cell default-cell ${styles.cardTableCell} ${styles.defaultCell}`}
        >
          Records Failed
        </div>

        <div
          className={`card-table-cell default-cell ${styles.cardTableCell} ${styles.defaultCell}`}
        >
          Failure Stage
        </div>

        <div className={`card-table-cell error-cell ${styles.cardTableCell} ${styles.errorCell}`}>
          Failure Reason
        </div>

        <div className={`card-table-cell d-flex justify-end action ${styles.cardTableCell} ${styles.defaultCell}`}>
          {showDownloadOption && (
            <>
              {!downloading && (
                <HdRbacButton
                  rbacPermission={RbacPermissions.ACTIVATION_EDIT}
                  size='sm'
                  variation='flat'
                  icon='download'
                  edge='end'
                  type='button'
                  disabled={!syncRecordFailures.length}
                  onClick={downloadErrorReport}
                  dataId={dataIdGenerator('download')}
                >
                  Report
                </HdRbacButton>
              )}

              {downloading && (
                <div className='center-flex-row'>
                  <div className='text-medium'>Downloading...</div>

                  <HdDeterminateProgressBar
                    className={`${styles.downloadProgressBar} ml-3 mr-2`}
                    value={downloadPercentage}
                    maxValue={100}
                    color='success'
                    showPercentage
                  />

                  <HdIconButton
                    onClick={cancelDownloadReport}
                    className='p-1'
                    dataId={dataIdGenerator('cancel-download')}
                  >
                    <HdIcon name='close' size={3} />
                  </HdIconButton>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {loading && <div className={styles.cardTableRow}>Loading Details...</div>}

      {!loading && syncRecordFailures.length <= 0 && !error && (
        <div className={styles.cardTableRow}>No Records Found</div>
      )}

      {error && <div className={`${styles.cardTableRow} text-error`}>{error}</div>}

      {syncRecordFailures.length > 0 && (
        <div className={styles.recordsFailureList} id={dataIdGenerator('records-failure-list')}>
          {syncRecordFailures.map((syncRecordFailure, index) => (
            <div className={`${styles.cardTableRow} center-flex-row`} key={index}>
              <div className={`${styles.cardTableCell} ${styles.defaultCell} text-medium`}>
                {numFormatter(syncRecordFailure.recordCount)}
              </div>

              <div className={`${styles.cardTableCell} ${styles.defaultCell} text-medium`}>
                {getRunFailureStage(syncRecordFailure)}
              </div>

              <div className={`${styles.cardTableCell} ${styles.errorCell} text-normal`}>
                {syncRecordFailure.error}
              </div>

              <div className={`${styles.cardTableCell} action`}>
                <HdTooltip title='View 1 sample'>
                  <HdRbacButton
                    rbacPermission={RbacPermissions.ACTIVATION_EDIT}
                    size='sm'
                    variation='flat'
                    edge='end'
                    icon='sample-event'
                    type='button'
                    onClick={() => showSample(syncRecordFailure.sampleData)}
                    dataId={dataIdGenerator(`sample-${index}`)}
                  >
                    Sample
                  </HdRbacButton>
                </HdTooltip>
              </div>
            </div>
          ))}

          {totalFailuresCount && isTotalFailuresCountMoreThan100 && (
            <div className='center-flex-row justify-center p-3'>
              Showing top {syncRecordFailures.length} of {numFormatter(totalFailuresCount)} rows,
              for further details please use query or download report.
              <HdButton
                type='button'
                size='sm'
                variation='flat'
                icon='new-window'
                direction='right'
                className='text-capitalize ml-2'
                onClick={() => queryRecordsClick()}
                dataId={dataIdGenerator('query')}
              >
                Query {failureScope} Records
              </HdButton>
            </div>
          )}

          <ScrollToTopButton scrollViewportId={dataIdGenerator('records-failure-scroll-to-top')} />
        </div>
      )}

      <HdModal
        onClose={() => setSyncRecordFailureSampleData(false)}
        dataId={dataIdGenerator('modal')}
        placement='top'
        styling={{ width: '760px' }}
        open={syncRecordFailureSampleData}
        variant='custom'
        onTransitionEnd={() => {
          editorRef.current.refresh();
        }}
      >
        <PayloadViewerDialog
          getPayloadPromise={() =>
            Promise.resolve({
              payload: syncRecordFailureSampleData
            })
          }
          title='Activation Run Failure'
          subtitle='Sample Record'
          onNegativeClick={() => setSyncRecordFailureSampleData(null)}
          editorRef={editorRef}
        />
      </HdModal>

      <ConfirmDialogV1
        open={isCancelDownloadDialogOpen}
        title='Are you sure you want to cancel the download?'
        body='Your reports are being downloaded and it require some time for us to complete the process.'
        positiveButtonText='Yes, Cancel Download '
        negativeButtonText='No, Continue Download'
        onClose={() => setIsCancelDownloadDialogOpen(false)}
        onPositiveClickCallback={cancelDownload}
      />
    </div>
  );
}

export default RecordsFailure;
